import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const networkQualificationArticle: BlogArticle = {
  key: BlogArticleKey.NETWORK_QUALIFICATION,
  slug: BlogSlugs.NETWORK_QUALIFICATION,
  categoryId: "03",
  date: "2026-08-29",
  readTime: 7,
  locales: {
    fr: {
      title: "Qualifier son réseau LinkedIn plutôt qu'acheter une liste",
      excerpt:
        "Ton réseau contient déjà des gens prêts à acheter. Le vrai problème n'est pas de trouver des prospects, c'est de trier ceux que tu possèdes déjà.",
      description:
        "Ton réseau LinkedIn contient déjà tes prochains clients. Comment qualifier tes relations avec des critères tirés de tes appels.",
      ctaTitle: "Trie ton réseau au lieu d'acheter une liste",
      ctaText:
        "On part de tes appels clients pour écrire tes critères, puis on qualifie tes relations une par une en expliquant pourquoi chacune colle. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tu as passé des années à accepter des invitations. Deux mille relations, parfois cinq mille. Et le jour où tu dois prospecter, tu ouvres Sales Navigator, tu achètes une base, ou tu pars scraper des profils que tu n'as jamais croisés.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pendant ce temps, la liste la plus qualifiée que tu possèdes dort dans ton propre compte. Personne ne la trie, parce que personne ne sait par quel bout la prendre.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ton réseau est une base de données que tu n'as jamais ouverte",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une relation LinkedIn n'est pas un inconnu. C'est quelqu'un qui a accepté ton invitation, ou dont tu as accepté la sienne. Il y a eu un contexte : un événement, un client commun, un contenu, un ancien poste. Ce lien existe déjà, et il ne te coûte rien.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un prospect acheté ne te connaît pas. Une relation, si. C'est la seule liste où tu ne pars pas d'un taux de réponse nul.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème n'est donc pas le volume. Sur deux mille relations, quelques dizaines vivent probablement la situation exacte que ton offre adresse. Le problème, c'est que tu ignores lesquelles, et que rien dans LinkedIn ne te le dira.",
        },
        { type: ArticleBlockType.H2, text: "Pourquoi personne ne prospecte son propre réseau" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Ce n'est pas triable à la main.** Deux mille profils à trois minutes de lecture, c'est cent heures. Personne ne les a.",
            "**Les filtres LinkedIn ne filtrent pas ce qui compte.** Tu peux trier par poste, par entreprise, par secteur. Aucun de ces champs ne dit si la personne a le problème que tu résous.",
            "**Tes critères ne sont écrits nulle part.** Tant que tu ne sais pas formuler ce qui sépare un bon client d'un mauvais, il n'y a rien à chercher.",
            "**Ça ne ressemble pas à de la prospection.** Acheter une base donne l'impression d'agir. Relire ses propres relations donne l'impression de tourner en rond.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les trois premiers points sont des problèmes d'outillage. Le quatrième est un biais, et c'est le plus cher des quatre.",
        },
        { type: ArticleBlockType.H2, text: "Acheter une liste ou trier la tienne" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Une liste achetée",
            items: [
              "Des gens qui ne t'ont jamais vu",
              "Des données figées, souvent périmées",
              "Un coût par contact, avant la moindre réponse",
              "Un premier message qui doit tout justifier",
              "Une conformité à démontrer sur des contacts que tu n'as pas collectés",
            ],
          },
          good: {
            title: "Ton réseau qualifié",
            items: [
              "Des gens qui ont déjà accepté un lien",
              "Des profils que leur propriétaire tient à jour",
              "Une base que tu possèdes déjà",
              "Un premier message qui s'appuie sur ce lien",
              "Des profils publics, sur un réseau que tu as construit",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce n'est pas un plaidoyer contre les bases de données. Il y a des cas où tu dois sortir de ton réseau, et c'est très bien. Mais commencer par l'extérieur quand l'intérieur n'a jamais été trié, c'est payer pour de l'inconnu avant d'avoir lu ce que tu as déjà.",
        },
        { type: ArticleBlockType.H2, text: "Ce qu'il faut pour trier un réseau" },
        { type: ArticleBlockType.H3, text: "1. Des critères écrits, pas une intuition" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tant que ton client idéal reste une impression, tu ne peux rien trier : chaque profil se discute. Un critère utilisable est une phrase qu'on confronte à un profil et qu'on tranche par oui ou par non. *Dirige une équipe commerciale de trois à quinze personnes.* *A changé de poste dans les six derniers mois.* *Vend un service, pas un produit physique.*",
        },
        { type: ArticleBlockType.H3, text: "2. Des critères qui viennent de tes clients" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les critères que tu écris de mémoire décrivent le client que tu voudrais. Ceux qui fonctionnent décrivent celui qui a signé. La différence est dans tes appels : les gens qui achètent racontent leur situation avant d'acheter, et ils la racontent avec des mots qui reviennent. Ce sont ces récurrences qui font des critères.",
        },
        { type: ArticleBlockType.H3, text: "3. Une lecture profil par profil, et une raison" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un tri qui rend une liste sans dire pourquoi ne sert à rien : tu ne peux ni le vérifier, ni t'en servir pour écrire. Chaque profil retenu doit arriver avec ce qui l'a fait retenir, une ligne de son parcours, une phrase de sa description, un changement récent. Cette raison est aussi ton premier message.",
        },
        { type: ArticleBlockType.H2, text: "Le message change complètement" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quand tu contactes une relation dont tu sais pourquoi elle est là, tu n'ouvres plus en te présentant. Tu ouvres sur ce que tu as vu. Le lien existe déjà, le contexte tient en une phrase, et la question qui suit devient légitime.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Sur une liste achetée", "Sur une relation qualifiée"],
          rows: [
            [
              "Une ouverture qui présente ton entreprise",
              "Une ouverture sur le lien et sur ce que tu as vu",
            ],
            [
              "L'offre dès la deuxième ligne",
              "La situation repérée, et pourquoi elle t'a fait écrire",
            ],
            [
              "Un rendez-vous demandé sans raison précise",
              "Une question à laquelle on répond en une ligne",
            ],
            ["Le même texte pour toute la liste", "Un texte qui ne marche que pour cette personne"],
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On part de tes appels clients pour construire ton avatar réel, puis on en tire les critères de ceux qui achètent vraiment. L'extension lit ensuite les profils de ton réseau, les confronte à ces critères et remonte ceux qui collent, avec la raison pour chacun. Tu ne cherches plus qui contacter, tu lis une liste déjà justifiée.",
          linkLabel: "Découvrir la recherche de prospects",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant d'acheter une base, ouvre celle que tu as déjà. Écris tes critères à partir de tes appels plutôt que de mémoire, fais-les passer sur tes relations une par une, et garde la raison qui a fait retenir chaque profil. Tu obtiendras une liste plus courte, plus chaude, et un premier message qui s'écrit presque tout seul.",
        },
      ],
      faq: [
        {
          question: "Comment trouver des prospects dans son réseau LinkedIn ?",
          answer:
            "En partant de critères écrits plutôt que des filtres LinkedIn. Les filtres trient par poste, entreprise et secteur, or aucun de ces champs ne dit si la personne a le problème que tu résous. Il faut des critères tirés de tes clients actuels, puis confronter chaque relation à ces critères et garder la raison qui l'a fait retenir.",
        },
        {
          question: "Vaut-il mieux prospecter son réseau ou acheter une liste ?",
          answer:
            "Commence par ton réseau. Une relation a déjà accepté un lien avec toi, son profil est tenu à jour par son propriétaire, et elle ne te coûte rien. Une liste achetée a sa place, mais après avoir lu ce que tu possèdes déjà, pas avant.",
        },
        {
          question: "Combien de relations faut-il pour que ça vaille le coup ?",
          answer:
            "Quelques centaines suffisent. Sur cinq cents relations, il y a en général assez de profils correspondant à ta cible pour remplir plusieurs semaines de prospection. Ce qui compte n'est pas la taille du réseau, c'est la précision des critères qu'on lui applique.",
        },
        {
          question: "Comment écrire des critères de ciblage utilisables ?",
          answer:
            "Un critère utilisable se tranche par oui ou par non en lisant un profil. *Dirige une équipe de trois à quinze commerciaux* en est un, *entreprise en croissance* n'en est pas un. Et il vient de ce que racontent tes clients dans tes appels, pas de ce que tu imagines de ta cible.",
        },
      ],
    },
    en: {
      title: "Qualify your LinkedIn network instead of buying a list",
      excerpt:
        "Your network already holds people ready to buy. The real problem is not finding prospects, it is sorting the ones you already own.",
      description:
        "Your LinkedIn network already holds your next customers. How to qualify your connections with criteria drawn from your calls, instead of buying a list.",
      ctaTitle: "Sort your network instead of buying a list",
      ctaText:
        "We start from your customer calls to write your criteria, then qualify your connections one by one and explain why each one fits. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You have spent years accepting invitations. Two thousand connections, sometimes five thousand. And the day you need to prospect, you open Sales Navigator, you buy a database, or you go and scrape profiles you have never crossed paths with.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Meanwhile, the most qualified list you own is sitting in your own account. Nobody sorts it, because nobody knows where to start.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Your network is a database you have never opened",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A LinkedIn connection is not a stranger. It is someone who accepted your invitation, or whose invitation you accepted. There was a context: an event, a shared client, a piece of content, a former role. That link already exists, and it costs you nothing.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A bought prospect does not know you. A connection does. It is the only list where you do not start from a reply rate of zero.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "So the problem is not volume. Out of two thousand connections, a few dozen are probably living the exact situation your offer addresses. The problem is that you do not know which ones, and nothing in LinkedIn will tell you.",
        },
        { type: ArticleBlockType.H2, text: "Why nobody prospects their own network" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**It cannot be sorted by hand.** Two thousand profiles at three minutes of reading each is a hundred hours. Nobody has them.",
            "**LinkedIn filters do not filter what matters.** You can sort by role, company and industry. None of those fields tells you whether the person has the problem you solve.",
            "**Your criteria are written nowhere.** Until you can state what separates a good customer from a bad one, there is nothing to search for.",
            "**It does not feel like prospecting.** Buying a database feels like taking action. Rereading your own connections feels like going in circles.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The first three are tooling problems. The fourth is a bias, and it is the most expensive of the four.",
        },
        { type: ArticleBlockType.H2, text: "Buying a list or sorting your own" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "A bought list",
            items: [
              "People who have never seen you",
              "Frozen data, often out of date",
              "A cost per contact, before a single reply",
              "A first message that has to justify everything",
              "Compliance to prove on contacts you did not collect",
            ],
          },
          good: {
            title: "Your qualified network",
            items: [
              "People who already accepted a link",
              "Profiles kept up to date by their owner",
              "A base you already own",
              "A first message that leans on that link",
              "Public profiles, on a network you built",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is not a case against databases. There are situations where you have to step outside your network, and that is fine. But starting outside when the inside has never been sorted means paying for strangers before reading what you already have.",
        },
        { type: ArticleBlockType.H2, text: "What it takes to sort a network" },
        { type: ArticleBlockType.H3, text: "1. Written criteria, not a gut feeling" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "As long as your ideal customer stays an impression, you cannot sort anything: every profile is debatable. A usable criterion is a sentence you hold against a profile and settle with a yes or a no. *Runs a sales team of three to fifteen people.* *Changed roles in the last six months.* *Sells a service, not a physical product.*",
        },
        { type: ArticleBlockType.H3, text: "2. Criteria that come from your customers" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The criteria you write from memory describe the customer you wish you had. The ones that work describe the customer who signed. The difference lives in your calls: people who buy describe their situation before buying, and they describe it with words that keep coming back. Those recurrences are what make criteria.",
        },
        { type: ArticleBlockType.H3, text: "3. A profile by profile read, and a reason" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A sort that hands you a list without saying why is useless: you can neither check it nor write from it. Every profile you keep must arrive with what made you keep it, a line of their career, a sentence from their description, a recent change. That reason is also your first message.",
        },
        { type: ArticleBlockType.H2, text: "The message changes entirely" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "When you contact a connection and know why they are on the list, you no longer open by introducing yourself. You open on what you saw. The link already exists, the context fits in one sentence, and the question that follows becomes legitimate.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["On a bought list", "On a qualified connection"],
          rows: [
            ["An opener that introduces your company", "An opener on the link and on what you saw"],
            [
              "The offer by the second line",
              "The situation you spotted, and why it made you write",
            ],
            [
              "A meeting asked for with no clear reason",
              "A question that can be answered in one line",
            ],
            ["The same text for the whole list", "A text that only works for this person"],
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We start from your customer calls to build your real client avatar, then draw the criteria of the people who actually buy. The extension then reads the profiles in your network, holds them against those criteria and surfaces the ones that fit, with the reason for each. You no longer look for who to contact, you read a list that already explains itself.",
          linkLabel: "Discover prospect search",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before buying a database, open the one you already have. Write your criteria from your calls rather than from memory, run them across your connections one by one, and keep the reason that made you retain each profile. You will end up with a shorter, warmer list, and a first message that almost writes itself.",
        },
      ],
      faq: [
        {
          question: "How do you find prospects inside your LinkedIn network?",
          answer:
            "By starting from written criteria rather than LinkedIn filters. Filters sort by role, company and industry, and none of those fields tells you whether the person has the problem you solve. You need criteria drawn from your current customers, then hold every connection against them and keep the reason that made you retain it.",
        },
        {
          question: "Is prospecting your network better than buying a list?",
          answer:
            "Start with your network. A connection already accepted a link with you, their profile is kept up to date by its owner, and it costs you nothing. A bought list has its place, but after you have read what you already own, not before.",
        },
        {
          question: "How many connections do you need for this to be worth it?",
          answer:
            "A few hundred is enough. Out of five hundred connections there are usually enough matching profiles to fill several weeks of prospecting. What matters is not the size of the network, it is the precision of the criteria you apply to it.",
        },
        {
          question: "How do you write usable targeting criteria?",
          answer:
            "A usable criterion is settled with a yes or a no while reading a profile. *Runs a sales team of three to fifteen people* is one, *growing company* is not. And it comes from what your customers say in your calls, not from what you imagine about your target.",
        },
      ],
    },
  },
};
